import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useExperiences } from 'core/contexts/experienceV2';
import { environmental_policies, ExperienceStatus, getCancelPolicyLabel, getTranslatedName, includes_policies, PayNow, validateRequiredString } from '@utriper/core';
import { CircularIndeterminate, DropdownButton, GoogleMap, TimeDurationFormatter } from '@utriper/core-ui';
import Layout from 'components/Layout';
import { Section } from 'modules/Pricing/styles';
import MediaCarousel from './components/MediaCarousel';
import { Fade, Grid2, Typography, useMediaQuery } from '@mui/material';
import { InfoSection, Moduses, PriceDuration, SectionTitle, Text, TextCheck } from './styles';
import UpdateStatusForm from '../components/UpdateStatusForm';
import ExperienceStatusChip from 'components/ExperienceStatusChip';

const ExperienceProfilePage = () => {
    const { id } = useParams();
    const experienceId = parseInt(id);
    const history = useHistory();
    const { t: translate, i18n } = useTranslation();
    const { getExperienceById, experience, loading } = useExperiences();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const [openStatusForm, setOpenStatusForm] = useState(false);
    const [statusToUpdate, setStatusToUpdate] = useState(null);

    useEffect(() => {
        getExperienceById(experienceId, history);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceId]);

    const hasModuses = useMemo(() => Array.isArray(experience?.moduses) && experience?.moduses?.length > 0, [experience?.moduses]);

    const hasPayNow = useMemo(() => PayNow.validatePayNow(experience?.payNowType, experience?.payNow), [experience?.payNowType, experience?.payNow]);

    const hasCancelPolicies = useMemo(() => Array.isArray(experience?.cancelPolicies) && experience?.cancelPolicies?.length > 0, [experience?.cancelPolicies]);
    const payNowLabel = useMemo(() => {
        if (!hasPayNow) return '';
        const value = PayNow.getPayNowLabel(experience.payNowType, experience.payNow, i18n.language);
        const totalLabel = PayNow.isTypePercentage(experience.payNowType) ? translate('experience_profile.pay_now_label.total') : '';
        const label = `${value} ${totalLabel}— ${translate('experience_profile.pay_now_label.book')}.`;
        return label;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPayNow, experience?.payNowType, experience?.payNow, i18n.language]);
    const envPolicies = useMemo(() => {
        if (!Array.isArray(experience?.envPolicies) || experience?.envPolicies?.length <= 0) return [];

        const policies = experience.envPolicies.map(({ id, description }) => ({
            id,
            label: id === 5 ? description : getTranslatedName(environmental_policies, id, i18n.language),
        }));

        return policies;
    }, [experience?.envPolicies, i18n.language]);

    // TODO: MOVE THIS INTO A COMPONENT
    const actionButtons = useMemo(() => {
        let status = ExperienceStatus.IDS.live;

        if (experience?.status === status) {
            status = ExperienceStatus.IDS.archived;
        }

        const actions = [
            {
                id: 'edit',
                label: translate('experience_id.actions.edit'),
                onClick: () => history.push(`/experience/${experienceId}/edit`),
            },
            // {
            //     id: 'duplicate',
            //     label: translate('experience_id.actions.duplicate'),
            //     onClick: () => {},
            // },
            {
                id: status,
                label: experience?.status === ExperienceStatus.IDS.live ? translate('experience_id.actions.archive') : translate('experience_id.actions.publish'),
                onClick: () => {
                    setOpenStatusForm(true);
                    setStatusToUpdate(status);
                },
            },
        ];
        return actions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceId, experience?.status]);

    return (
        <Layout>
            {loading ? (
                <CircularIndeterminate centered />
            ) : (
                <Fade in={!loading} timeout={300}>
                    <Section style={{ overflow: 'visible' }}>
                        {/* Header */}
                        <Grid2 container size={12} justifyContent="space-between" alignItems="center">
                            {/* Title */}
                            <Grid2
                                container
                                size={10}
                                direction="column"
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                gap="8px"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313', width: '100%' }} noWrap>
                                    {experience?.name}
                                </Typography>
                                <ExperienceStatusChip status={experience?.status} />
                            </Grid2>

                            <Grid2 container size={2} justifyContent="flex-end">
                                <DropdownButton
                                    actions={actionButtons}
                                    variant="contained"
                                    buttonSx={{
                                        textTransform: 'none',
                                        backgroundColor: '#131313',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#333',
                                        },
                                    }}
                                    dropdownSx={{
                                        backgroundColor: '#131313',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#333',
                                        },
                                    }}
                                    menuItemSx={{
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0',
                                        },
                                    }}
                                    iconButton={isMobile}
                                    iconButtonSx={{
                                        backgroundColor: '#131313',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#333',
                                        },
                                        borderRadius: '4px',
                                    }}
                                />
                            </Grid2>
                        </Grid2>

                        <Grid2 container size={12} spacing={2} mt="32px">
                            {/* Column 1 */}
                            <Grid2 container size={{ xs: 12, md: 8 }} spacing={2} direction="column">
                                {/* Images */}
                                <Grid2 size={12} sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                                    <MediaCarousel experienceMedia={experience?.media} />
                                </Grid2>
                                {/* Subtitle */}
                                {validateRequiredString(experience?.description1, null) && (
                                    <Grid2 container size={12}>
                                        <SectionTitle>{experience?.description1}</SectionTitle>
                                    </Grid2>
                                )}
                                {/* Description */}
                                {validateRequiredString(experience?.description2, null) && (
                                    <InfoSection title={translate('experience_profile.description')}>
                                        <Text>{experience.description2}</Text>
                                    </InfoSection>
                                )}
                                {/* Moods */}
                                {hasModuses && <Moduses moduses={experience?.moduses} language={i18n.language} />}

                                {/* Price and Duration */}
                                <Grid2 display={{ xs: 'block', md: 'none' }}>
                                    <PriceDuration price={experience?.price} priceGroupId={experience?.priceGroupId} duration={experience?.duration} currency={experience?.currency} />
                                </Grid2>
                                {/* Location */}
                                {experience?.location && (
                                    <InfoSection title={translate('experience_profile.location')}>
                                        <Text>{experience.location}</Text>
                                    </InfoSection>
                                )}
                                {/* Meeting Point */}
                                {experience?.meetingPointData && (
                                    <InfoSection title={translate('experience_profile.meeting_point')}>
                                        <GoogleMap input={{ value: experience.meetingPointData }} mode="view" style={{ width: '100%' }} />
                                    </InfoSection>
                                )}
                                {/* Meeting Instructions */}
                                {validateRequiredString(experience?.meetingInstructions) && (
                                    <InfoSection title={translate('experience_profile.meeting_instructions')}>
                                        <Text>{experience.meetingInstructions}</Text>
                                    </InfoSection>
                                )}
                                {/* Anticipation Time */}
                                {Number.isFinite(experience?.anticipation) && (
                                    <InfoSection title={translate('experience_profile.anticipation')} hide>
                                        <Text>{experience.anticipation > 0 ? translate('experience_profile.anticipation_time', { time: TimeDurationFormatter(experience.anticipation, translate) }) : translate('experience_profile.no_anticipation')}</Text>
                                    </InfoSection>
                                )}
                                {/* Pay Now */}
                                {hasPayNow && (
                                    <InfoSection title={translate('experience_profile.pay_now')} hide>
                                        <Text>{payNowLabel}</Text>
                                    </InfoSection>
                                )}
                                {/* Cancel Policies */}
                                {hasCancelPolicies && (
                                    <InfoSection title={translate('experience_profile.refund')} hide>
                                        {experience.cancelPolicies.map(({ id, percentage, time }) => (
                                            <Text key={id}>{getCancelPolicyLabel(percentage, time, translate)}</Text>
                                        ))}
                                    </InfoSection>
                                )}
                                {/* Includes */}
                                {experience?.include?.length > 0 && (
                                    <InfoSection title={translate('experience_profile.includes')}>
                                        {experience.include.map(({ id, includesId, customLabel }) => {
                                            const label = Number.isInteger(includesId) ? getTranslatedName(includes_policies, includesId, i18n.language) : customLabel;

                                            return <TextCheck key={id} label={label} />;
                                        })}
                                    </InfoSection>
                                )}
                                {/* Not Includes */}
                                {experience?.notInclude?.length > 0 && (
                                    <InfoSection title={translate('experience_profile.not_includes')}>
                                        {experience.notInclude.map(({ id, includesId, customLabel }) => {
                                            const label = Number.isInteger(includesId) ? getTranslatedName(includes_policies, includesId, i18n.language) : customLabel;

                                            return <TextCheck key={id} checked={false} label={label} />;
                                        })}
                                    </InfoSection>
                                )}
                                {/* Env Policies */}
                                {envPolicies?.length > 0 && (
                                    <InfoSection title={translate('experience_profile.env_policies')}>
                                        {envPolicies.map(({ id, label }) => (
                                            <TextCheck key={id} label={label} />
                                        ))}
                                    </InfoSection>
                                )}
                                {/* Checkin */}
                                <InfoSection title="Check-in" hide>
                                    <TextCheck checked={experience?.requireId} label={translate('experience_profile.check_in', { required: !experience?.requireId ? 'No' : '' })} />
                                </InfoSection>
                                {/* Pax Message */}
                                {validateRequiredString(experience?.paxMessage, null) && (
                                    <InfoSection title={translate('experience_profile.pax_message')} hide>
                                        <Text>{experience.paxMessage}</Text>
                                    </InfoSection>
                                )}
                            </Grid2>

                            {/* Column 2 Desktop */}
                            <Grid2 container display={{ xs: 'none', md: 'flex' }} sx={{ position: 'sticky', top: 24, alignSelf: 'start' }} size={4} spacing={2} direction="column">
                                {/* Price and Duration */}
                                <PriceDuration price={experience?.price} priceGroupId={experience?.priceGroupId} duration={experience?.duration} currency={experience?.currency} />
                                {/* Anticipation Time */}
                                {Number.isFinite(experience?.anticipation) && (
                                    <InfoSection title={translate('experience_profile.anticipation')}>
                                        <Text>{experience.anticipation > 0 ? translate('experience_profile.anticipation_time', { time: TimeDurationFormatter(experience.anticipation, translate) }) : translate('experience_profile.no_anticipation')}</Text>
                                    </InfoSection>
                                )}
                                {/* Pay Now */}
                                {hasPayNow && (
                                    <InfoSection title={translate('experience_profile.pay_now')}>
                                        <Text>{payNowLabel}</Text>
                                    </InfoSection>
                                )}
                                {/* Cancel Policies */}
                                {hasCancelPolicies && (
                                    <InfoSection title={translate('experience_profile.refund')}>
                                        {experience.cancelPolicies.map(({ id, percentage, time }) => (
                                            <Text key={id}>{getCancelPolicyLabel(percentage, time, translate)}</Text>
                                        ))}
                                    </InfoSection>
                                )}
                                {/* Checkin */}
                                <InfoSection title="Check-in">
                                    <TextCheck checked={experience?.requireId} label={translate('experience_profile.check_in', { required: !experience?.requireId ? 'No' : '' })} />
                                </InfoSection>
                                {/* Pax Message */}
                                {validateRequiredString(experience?.paxMessage, null) && (
                                    <InfoSection title={translate('experience_profile.pax_message')}>
                                        <Text>{experience.paxMessage}</Text>
                                    </InfoSection>
                                )}
                            </Grid2>
                        </Grid2>
                    </Section>
                </Fade>
            )}

            {openStatusForm && (
                <UpdateStatusForm
                    open={openStatusForm}
                    handleClose={() => {
                        setOpenStatusForm(false);
                        setStatusToUpdate(null);
                    }}
                    status={statusToUpdate}
                    experienceId={experienceId}
                    experienceIds={[]}
                />
            )}
        </Layout>
    );
};

export default ExperienceProfilePage;
